import React, { memo, useCallback, useContext, useMemo } from 'react';
import Draft from 'draft-js';
import { useDebounce } from '@components';
import { DatePicker, Input } from 'antd';
import Date from './DotDescriptionEditor';
import configCtx from '../services/context.service';
import moment from 'moment';
import RichEditor from '@modules/questionnaires/complexity/richEditor';
import { RICH_TYPE } from '@components/richEditor/configs';

const getTitleFontSize = (value) => {
  const defaultSize = '1rem';
  const smallSize = '0.8rem';
  let plainText = value;
  if (typeof value === 'string') {
    try {
      const parsedObject = JSON.parse(value);
      if (Object.keys(parsedObject).includes('blocks')) {
        const draftState = Draft.convertFromRaw(parsedObject);
        plainText = draftState.getPlainText();
      }
    } catch (e) {
      console.log('parse fail', e);
    }
  }
  return plainText?.length > 30 ? smallSize : defaultSize;
};

const HeaderTitle = ({
  id,
  isExpand,
  title,
  onExpand,
  isEditing,
  order,
  changeTreeDataCollection,
  handleDeletePhase,
  formChange = () => {},
}) => {
  const fontSize = useMemo(
    () => (isEditing ? undefined : getTitleFontSize(title)),
    [isEditing, JSON.stringify(title)],
  );
  const debouncedChangeTreeDataCollection = useDebounce((payload) => {
    if (payload.title !== title) {
      changeTreeDataCollection({ location: [order], payload });
    }
  }, 300);

  // to prevent new line in rich text editor
  const keyBindingFn = useCallback((e) => {
    if (e.keyCode === 13) {
      return false;
    }

    return Draft.getDefaultKeyBinding(e);
  }, []);

  return (
    <div className="header">
      {isEditing ? (
        <div className="title-input-wrapper">
          {/*<Input*/}
          {/*  maxLength={50}*/}
          {/*  className="title-input"*/}
          {/*  defaultValue={title}*/}
          {/*  onChange={(e) => {*/}
          {/*    debouncedChangeTreeDataCollection({ title: e.target.value });*/}
          {/*  }}*/}
          {/*/>*/}
          <RichEditor
            value={title}
            richTypes={[RICH_TYPE.LINK]}
            onChange={(value) => {
              debouncedChangeTreeDataCollection({ title: value });
            }}
            keyBindingFn={keyBindingFn}
            maxLength={50}
            linkPopoverProps={{
              // placement: 'rightTop',
              overlayClassName:
                'lifecycle-link-popover guiding-principle-link-adjust',
            }}
            formChange={formChange}
          />
          <button
            className="phase-delete-icon appkiticon icon-delete-fill"
            onClick={() => handleDeletePhase(id)}
          />
        </div>
      ) : (
        <>
          <div className="icon-wrapper" onClick={onExpand}>
            <span
              className={`appkiticon icon-${isExpand ? 'minus' : 'plus'}-fill`}
            />
          </div>
          <div className="text" style={{ fontSize }}>
            <RichEditor value={title} readOnly />
          </div>
          {/*<span className="text" style={{ fontSize: fontSize }}>{title}</span>*/}
        </>
      )}
    </div>
  );
};
const Dot = ({
  type,
  className,
  stepDes = {},
  isEditing,
  order,
  changeTreeDataCollection,
  isActive,
  initialDate,
  formChange = () => {},
}) => {
  const { name, defaultName, ...restStepDes } = stepDes;
  const debouncedChangeTreeDataCollection = useDebounce((payload) => {
    if (payload.name !== name) {
      changeTreeDataCollection({ location: [order], payload });
    }
  }, 300);

  const handleInputChange = (e) => {
    formChange();
    debouncedChangeTreeDataCollection({ name: e.target.value });
  };
  return (
    <div className={`dot-wrapper ${className ? className : ''}`}>
      <>
        <div className="dot">
          {initialDate}
          <div className={`dot-date-wrapper_${type}`}>
            {isEditing ? (
              <Date
                {...restStepDes}
                order={order}
                size="small"
                changeTreeDataCollection={changeTreeDataCollection}
                formChange={formChange}
              />
            ) : restStepDes.startAt ? (
              restStepDes.startAt.format('MM/DD/YY')
            ) : null}
          </div>
          <div className={`dot-des_${type}`}>
            {isEditing ? (
              <Input
                size="small"
                className="dot-des-input"
                maxLength={25}
                defaultValue={defaultName}
                onChange={handleInputChange}
              />
            ) : (
              name
            )}
          </div>
          <div className={`sub-dot ${isActive ? 'is-active' : ''}`} />
        </div>
      </>
    </div>
  );
};

const TreeHeader = ({
  id,
  type,
  stepDes,
  isFirst,
  isLast,
  isAllEditing,
  order,
  changeTreeDataCollection,
  onClick,
  isActive,
  ...restProps
}) => {
  const { handleDeletePhase, updateModuleFormRefBeginEndDate, section } =
    useContext(configCtx);
  let dateRange = [];
  if (type === 'Integration') {
    function findNearestPrevDate(arr, index, result) {
      if (result !== null || index === 0) {
        return result === null ? null : result.endOf('day');
      }
      index--;
      return findNearestPrevDate(arr, index, arr[index]);
    }

    dateRange = [
      findNearestPrevDate(
        stepDes.allPhaseDates,
        stepDes.allPhaseDates.length,
        null,
      ),
      null,
    ];
  } else {
    function findNearestNextDate(arr, index, result) {
      if (result !== null || index === arr.length - 1) {
        return result === null ? null : result.startOf('day');
      }
      index++;
      return findNearestNextDate(arr, index, arr[index]);
    }
    dateRange = [null, findNearestNextDate(stepDes.allPhaseDates, -1, null)];
  }
  const disabledDate = (current) => {
    if (dateRange[0] && dateRange[1]) {
      return (current && current <= dateRange[0]) || current >= dateRange[1];
    } else if (dateRange[0]) {
      return current && current <= dateRange[0];
    } else if (dateRange[1]) {
      return current && current >= dateRange[1];
    }

    return false;
  };
  const startEndDateDescription = (
    <div className="section-info">
      <div className="section-info-date">
        {isAllEditing ? (
          <DatePicker
            defaultValue={section.date ? moment(section.date) : ''}
            disabledDate={disabledDate}
            onChange={(e) => updateModuleFormRefBeginEndDate(e, 'date')}
            format="MM/DD/YY"
            placeholder=""
            size="small"
            autoFocus={false}
            suffixIcon={<span className="appkiticon icon-calendar-fill" />}
            getPopupContainer={() =>
              document.getElementsByClassName(
                'lifecycle-phases-container',
              )[0] || document.body
            }
          />
        ) : section.date ? (
          moment(section.date).format('MM/DD/YY')
        ) : (
          ''
        )}
      </div>
      <div className="section-info-description">
        {isAllEditing ? (
          <Input
            size="small"
            className="dot-des-input"
            maxLength={25}
            defaultValue={section.description}
            onChange={(e) => updateModuleFormRefBeginEndDate(e, 'description')}
          />
        ) : (
          section.description
        )}
      </div>
    </div>
  );
  const initialDate =
    isFirst && type === 'Divestiture' && startEndDateDescription;
  const endDate = isLast && type === 'Integration' && startEndDateDescription;
  return (
    <div style={{ display: 'flex', 'align-items': 'center' }}>
      <div style={{ width: '100%', 'text-align': 'end' }}>
        <div
          className="lifecycle-header-wrapper"
          onClick={(e = {}) => {
            if (e.target?.className.includes('last-dot')) {
              return;
            }
            onClick();
          }}
        >
          <Dot
            type={type}
            isActive={isActive}
            className={isFirst ? 'first-dot-wrapper' : ''}
            initialDate={initialDate}
            order={order}
            stepDes={stepDes}
            isEditing={isAllEditing}
            changeTreeDataCollection={changeTreeDataCollection}
            formChange={restProps.formChange}
          />
          <HeaderTitle
            {...restProps}
            id={id}
            order={order}
            isEditing={isAllEditing}
            changeTreeDataCollection={changeTreeDataCollection}
            handleDeletePhase={handleDeletePhase}
          />
          {isLast && (
            <div className="dot-wrapper last-dot-wrapper">
              <div className="dot last-dot">
                {endDate}
                <span className="last-dot appkiticon icon-check-mark-fill a-font-10" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TreeHeader);
